/**
 * 送信元アカウント追加ダイアログ（Phase 26）。
 *
 * 1 ステップ目: 「Gmail（OAuth）」/「SMTP」を選択
 * 2 ステップ目:
 *   - Gmail: 表示名のみ入力 → OAuth ポップアップ起動
 *   - SMTP : フォーム入力 → 接続検証 → 保存
 */
import { ArrowLeft, Mail, Server } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useCreateSmtpAccount, useStartGmailOAuth } from '@/hooks/useMailAccounts';

type Step = 'choose' | 'gmail' | 'smtp';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
}

export function AddAccountDialog({ open, onOpenChange, onCreated }: Props) {
  const [step, setStep] = useState<Step>('choose');

  useEffect(() => {
    if (open) setStep('choose');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {step !== 'choose' && (
              <button
                type="button"
                onClick={() => setStep('choose')}
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)] -ml-1"
                aria-label="戻る"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <DialogTitle>送信元アカウントを追加</DialogTitle>
          </div>
          <DialogDescription>
            {step === 'choose' && '追加するアカウントの種類を選択してください'}
            {step === 'gmail' && 'Gmail OAuth で認証します'}
            {step === 'smtp' && 'SMTP サーバーの情報を入力してください'}
          </DialogDescription>
        </DialogHeader>

        {step === 'choose' && <ChooseStep onSelect={setStep} />}
        {step === 'gmail' && (
          <GmailStep
            onClose={() => onOpenChange(false)}
            onCreated={onCreated}
          />
        )}
        {step === 'smtp' && (
          <SmtpStep
            onClose={() => onOpenChange(false)}
            onCreated={onCreated}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ChooseStep({ onSelect }: { onSelect: (step: Step) => void }) {
  return (
    <div className="grid grid-cols-1 gap-2 mt-2">
      <button
        type="button"
        onClick={() => onSelect('gmail')}
        className="flex items-center gap-3 p-4 rounded border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--accent-subtle)] text-left transition-colors"
      >
        <div className="rounded-full bg-[var(--accent-red-subtle)] p-2">
          <Mail className="size-5 text-[var(--accent-red)]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">Gmail（推奨）</div>
          <div className="text-xs text-[var(--text-secondary)] mt-0.5">
            Google アカウントで OAuth 認証 / 設定が最も簡単
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={() => onSelect('smtp')}
        className="flex items-center gap-3 p-4 rounded border border-[var(--border)] hover:border-[var(--accent)] hover:bg-[var(--accent-subtle)] text-left transition-colors"
      >
        <div className="rounded-full bg-[var(--accent-cyan-subtle)] p-2">
          <Server className="size-5 text-[var(--accent-cyan)]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold">SMTP（その他のメール）</div>
          <div className="text-xs text-[var(--text-secondary)] mt-0.5">
            Outlook / Yahoo / 自社サーバー等。host・port・パスワードを設定
          </div>
        </div>
      </button>
    </div>
  );
}

function GmailStep({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const startOAuth = useStartGmailOAuth();

  const handleStart = async () => {
    try {
      const res = await startOAuth.mutateAsync({
        displayName: displayName.trim() || undefined,
      });
      const popup = window.open(res.authUrl, 'gmail-oauth', 'width=500,height=700');
      if (!popup) {
        window.location.href = res.authUrl;
      }
      toast.success('Gmail 認証画面を開きました。完了したらこのダイアログを閉じてください');
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">表示名（任意）</Label>
          <Input
            type="text"
            placeholder="例：カウデザイン代表"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1">
            未入力なら認証完了時のメールアドレスをそのまま使います
          </p>
        </div>
        <div className="rounded bg-[var(--bg-elevated)] border border-[var(--border)] p-3 text-[11px] text-[var(--text-secondary)] leading-relaxed">
          Google アカウント選択画面に遷移します。「送信」「下書き作成」権限を許可してください。
          トークンは Cloud KMS で暗号化して保存されます。
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          キャンセル
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleStart}
          disabled={startOAuth.isPending}
        >
          {startOAuth.isPending && <Spinner className="size-3 mr-1" />}
          Google で認証する
        </Button>
      </DialogFooter>
    </>
  );
}

function SmtpStep({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated?: () => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState(587);
  const [secure, setSecure] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fromName, setFromName] = useState('');
  const createMutation = useCreateSmtpAccount();

  // port から secure を自動推定（465 → SMTPS, 587 → STARTTLS）
  const handlePortChange = (p: number) => {
    setPort(p);
    if (p === 465) setSecure(true);
    if (p === 587 || p === 25 || p === 2525) setSecure(false);
  };

  const handleSubmit = async () => {
    if (!displayName.trim() || !email.trim() || !host.trim() || !username.trim() || !password) {
      toast.error('すべての必須項目を入力してください');
      return;
    }
    try {
      await createMutation.mutateAsync({
        displayName: displayName.trim(),
        email: email.trim(),
        host: host.trim(),
        port,
        secure,
        username: username.trim(),
        password,
        fromName: fromName.trim() || undefined,
      });
      toast.success('SMTP アカウントを追加しました');
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <>
      <div className="space-y-3">
        <div>
          <Label className="text-xs">表示名 <span className="text-[var(--accent-red)]">*</span></Label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="例：カウデザイン info"
          />
        </div>
        <div>
          <Label className="text-xs">送信元メールアドレス（From） <span className="text-[var(--accent-red)]">*</span></Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="info@example.com"
          />
        </div>
        <div>
          <Label className="text-xs">From 表示名（任意）</Label>
          <Input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            placeholder="例：カウデザイン株式会社"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <Label className="text-xs">SMTP host <span className="text-[var(--accent-red)]">*</span></Label>
            <Input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.example.com"
            />
          </div>
          <div>
            <Label className="text-xs">port <span className="text-[var(--accent-red)]">*</span></Label>
            <Input
              type="number"
              value={port}
              onChange={(e) => handlePortChange(Number(e.target.value))}
            />
          </div>
        </div>

        <div>
          <Label className="text-xs flex items-center gap-2">
            <input
              type="checkbox"
              checked={secure}
              onChange={(e) => setSecure(e.target.checked)}
              className="size-3.5"
            />
            SSL/TLS（port 465 推奨）
          </Label>
          <p className="text-[11px] text-[var(--text-muted)] ml-6">
            未チェック = STARTTLS（port 587 推奨）
          </p>
        </div>

        <div>
          <Label className="text-xs">SMTP ユーザー名 <span className="text-[var(--accent-red)]">*</span></Label>
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="多くの場合は送信元メールと同じ"
          />
        </div>
        <div>
          <Label className="text-xs">SMTP パスワード <span className="text-[var(--accent-red)]">*</span></Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            placeholder="アプリパスワード推奨"
          />
          <p className="text-[11px] text-[var(--text-muted)] mt-1">
            KMS 暗号化で保存。保存前に SMTP 接続検証を行います。
          </p>
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          キャンセル
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending && <Spinner className="size-3 mr-1" />}
          検証して保存
        </Button>
      </DialogFooter>
    </>
  );
}
